import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ChevronLeft, ChevronRight } from "lucide-react";
import { GALLERY } from "@/data/site";
import SectionLabel from "./SectionLabel";

export default function Gallery() {
  const [active, setActive] = useState(null);

  const next = () => setActive((i) => (i + 1) % GALLERY.length);
  const prev = () => setActive((i) => (i - 1 + GALLERY.length) % GALLERY.length);

  return (
    <section
      id="galerie"
      className="relative py-24 md:py-32 bg-[#F9F6F0]"
      data-testid="gallery-section"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <SectionLabel
          eyebrow="Galerie"
          title={<>L'écrin<br /><span className="italic text-[#8B5A2B]">Harmony</span></>}
          subtitle="Un univers feutré pensé dans chaque détail — matières naturelles, lumière chaude et parfums envoûtants."
        />

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4 auto-rows-[180px] md:auto-rows-[260px]">
          {GALLERY.map((img, i) => (
            <motion.button
              key={i}
              onClick={() => setActive(i)}
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true, margin: "-50px" }}
              transition={{ duration: 0.7, delay: i * 0.07 }}
              className={`relative overflow-hidden rounded-xl group cursor-zoom-in ${img.span}`}
              data-testid={`gallery-image-${i}`}
            >
              <img
                src={img.src}
                alt={img.alt}
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1.2s] ease-out group-hover:scale-110"
              />
              <div className="absolute inset-0 bg-stone-900/0 group-hover:bg-stone-900/20 transition-colors duration-500" />
              <div className="absolute bottom-3 left-3 px-2 py-1 rounded-full bg-white/0 group-hover:bg-white/90 backdrop-blur-md text-stone-900 text-[10px] tracking-[0.2em] uppercase font-body opacity-0 group-hover:opacity-100 transition-all duration-500 translate-y-2 group-hover:translate-y-0">
                Agrandir
              </div>
            </motion.button>
          ))}
        </div>
      </div>

      {/* Lightbox */}
      <AnimatePresence>
        {active !== null && (
          <motion.div
            className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-stone-900/90 backdrop-blur-lg"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            data-testid="lightbox"
          >
            <button
              onClick={() => setActive(null)}
              className="absolute top-6 right-6 p-3 text-white/80 hover:text-white transition-colors"
              aria-label="Fermer"
              data-testid="lightbox-close"
            >
              <X strokeWidth={1.5} className="w-6 h-6" />
            </button>
            <button
              onClick={prev}
              className="absolute left-4 md:left-8 p-3 text-white/70 hover:text-white transition-colors"
              aria-label="Précédent"
              data-testid="lightbox-prev"
            >
              <ChevronLeft strokeWidth={1.5} className="w-7 h-7" />
            </button>
            <button
              onClick={next}
              className="absolute right-4 md:right-8 p-3 text-white/70 hover:text-white transition-colors"
              aria-label="Suivant"
              data-testid="lightbox-next"
            >
              <ChevronRight strokeWidth={1.5} className="w-7 h-7" />
            </button>
            <motion.img
              key={active}
              src={GALLERY[active].src}
              alt={GALLERY[active].alt}
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.97 }}
              transition={{ duration: 0.4 }}
              className="max-h-[85vh] max-w-[90vw] object-contain rounded-lg"
            />
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}
